/**
 * Pricing utility functions
 *
 * Helpers for converting between domain types and pricing calculation types
 */

import Dinero from "dinero.js";
import type { Quote, Service, CustomPart, Product } from "@onsemble/shared";
import type { QuoteTemplate } from "./schema";
import type { PricingInput } from "./pricing-types";

/**
 * Result type for service transformation
 */
export interface TransformedService {
  id: string;
  name: string;
  price: number;
}

/**
 * Converts Quote/QuoteTemplate data to PricingInput for pricing calculations
 *
 * This is the canonical transformation function - use this instead of manually
 * constructing PricingInput objects to ensure consistency and maintainability.
 *
 * @param quote - Quote, QuoteTemplate, or form data
 * @param products - Full product catalog data for price lookup
 * @param services - Service catalog for enrichment (optional)
 * @param customParts - Custom parts catalog for enrichment (optional)
 * @returns PricingInput ready for PricingService
 *
 * @example
 * // In React component with hooks
 * const input = quoteToPricingInput(quote, products, services, customParts);
 * const pricing = PricingService.calculatePricing(input);
 *
 * @example
 * // In non-React context (e.g., server-side)
 * const input = quoteToPricingInput(quote, products, services, customParts);
 * const result = PricingService.calculatePricing(input);
 */
export function quoteToPricingInput(
  quote: Quote | QuoteTemplate | any, // any allows form data for preview
  products: Product[],
  services?: Service[],
  customParts?: CustomPart[],
): PricingInput {
  // Transform discount from database format (discountPercentage + discountDescription)
  // to PricingService format (DiscountInfo object)
  let discount: PricingInput["discount"] = undefined;
  if (quote.discountPercentage && quote.discountPercentage > 0) {
    discount = {
      type: "percentage",
      value: quote.discountPercentage,
      description: quote.discountDescription || "",
    };
  }

  return {
    laborHours: quote.laborHours,
    laborRate: quote.laborRate,
    products: quote.products || [],
    productsData: products,
    markupPercent: quote.markupPercent,
    partsAffectPrice: quote.partsAffectPrice,
    services: services || [],
    quoteServices: quote.quoteServices || [],
    customParts: customParts || [],
    quoteCustomParts: quote.quoteCustomParts || [],
    discount,
    taxAmount: quote.taxAmount,
  };
}

/**
 * Transforms split service format to combined format for display/storage
 *
 * Converts form state (selectedServices + serviceCustomPrices + serviceCustomNames)
 * into the database format (quoteServices: [{id, name, price}])
 *
 * Used by:
 * - QuoteEditor preview to match database format
 * - Server endpoints when saving quotes/templates
 *
 * @param selectedServices - Array of service IDs
 * @param serviceCustomNames - Map of service IDs to custom names
 * @param serviceCustomPrices - Map of service IDs to custom prices (in cents)
 * @returns Array of service objects ready for database storage
 * @throws Error if any service is missing a name or price
 *
 * @example
 * const services = transformServicesForDisplay(
 *   ['s1', 's2'],
 *   { s1: 'Installation', s2: 'Repair' },
 *   { s1: 5000, s2: 3000 }
 * );
 * // Returns: [
 * //   { id: 's1', name: 'Installation', price: 5000 },
 * //   { id: 's2', name: 'Repair', price: 3000 }
 * // ]
 */
export function transformServicesForDisplay(
  selectedServices: string[],
  serviceCustomNames: Record<string, string>,
  serviceCustomPrices: Record<string, number>,
): TransformedService[] {
  let serviceData: TransformedService[] = [];

  if (selectedServices && selectedServices.length > 0) {
    // Verify that all services have names and prices
    for (const serviceId of selectedServices) {
      if (!serviceCustomNames || serviceCustomNames[serviceId] === undefined) {
        throw new Error(
          `Service ${serviceId} is missing a name. All services must have names.`,
        );
      }

      if (
        !serviceCustomPrices ||
        serviceCustomPrices[serviceId] === undefined
      ) {
        throw new Error(
          `Service ${serviceId} is missing a price. All services must have prices.`,
        );
      }
    }

    // Build service objects from selected IDs and custom values
    serviceData = selectedServices.map((serviceId: string) => {
      return {
        id: serviceId,
        name: serviceCustomNames[serviceId],
        price: serviceCustomPrices[serviceId],
      };
    });
  }

  return serviceData;
}

export function formatCentsAsDollars(cents: number): string {
  const money = Dinero({ amount: cents, currency: "USD" });
  return money.toFormat("0.00");
}
